// Module constants for each app
export const TRAVEL_MODULES = [
  'DASHBOARD',
  'CUSTOMERS',
  'PARTNERS',
  'PACKAGES',
  'QUOTES',
  'BOOKINGS',
  'INVOICES',
  'EXPENSES',
  'ALLOCATIONS',
  'OPERATIONS',
  'NOTIFICATIONS',
  'PROMO_CODES',
  'REVIEWS',
  'ACCOUNTING',
] as const;

export const TRADING_MODULES = [
  'DASHBOARD',
  // Gates the unified Cari Kartlar suite (cari cards, balances,
  // transactions, mizan) plus the legacy customer pages.
  'CUSTOMERS',
  'BRANDS',
  'MODELS',
  'PRODUCTS',
  'INQUIRIES',
  'WAREHOUSES',
  'STOCK_LEVELS',
  'STOCK_MOVEMENTS',
  'SUPPLIERS',
  'PURCHASE_ORDERS',
  'GOODS_RECEIPTS',
  'SALES_QUOTES',
  'SALES_ORDERS',
  'DELIVERIES',
  'INVOICES',
  'PAYMENTS',
  'SUPPLIER_INVOICES',
  'CREDIT_NOTES',
  'CONSIGNMENT',
  'ACCOUNTING',
] as const;

export const PERMISSION_ACTIONS = [
  'VIEW',
  'CREATE',
  'EDIT',
  'DELETE',
  'EXPORT',
  'APPROVE',
] as const;

export type TravelModule = (typeof TRAVEL_MODULES)[number];
export type TradingModule = (typeof TRADING_MODULES)[number];
export type PermissionActionType = (typeof PERMISSION_ACTIONS)[number];
export type PermissionModule = TravelModule | TradingModule;

// Module display names for UI
export const TRAVEL_MODULE_LABELS: Record<TravelModule, string> = {
  DASHBOARD: 'Dashboard',
  CUSTOMERS: 'Customers',
  PARTNERS: 'Partners',
  PACKAGES: 'Packages',
  QUOTES: 'Quotes',
  BOOKINGS: 'Bookings',
  INVOICES: 'Invoices',
  EXPENSES: 'Expenses',
  ALLOCATIONS: 'Allocations',
  OPERATIONS: 'Operations',
  NOTIFICATIONS: 'Notifications',
  PROMO_CODES: 'Promo Codes',
  REVIEWS: 'Reviews',
  ACCOUNTING: 'Accounting',
};

export const TRADING_MODULE_LABELS: Record<TradingModule, string> = {
  DASHBOARD: 'Dashboard',
  CUSTOMERS: 'Cari Kartlar (Customers)',
  BRANDS: 'Brands',
  MODELS: 'Models',
  PRODUCTS: 'Products',
  INQUIRIES: 'Inquiries',
  WAREHOUSES: 'Warehouses',
  STOCK_LEVELS: 'Stock Levels',
  STOCK_MOVEMENTS: 'Stock Movements',
  SUPPLIERS: 'Suppliers',
  PURCHASE_ORDERS: 'Purchase Orders',
  GOODS_RECEIPTS: 'Goods Receipts',
  SALES_QUOTES: 'Sales Quotes',
  SALES_ORDERS: 'Sales Orders',
  DELIVERIES: 'Deliveries',
  INVOICES: 'Invoices',
  PAYMENTS: 'Payments',
  SUPPLIER_INVOICES: 'Supplier Invoices',
  CREDIT_NOTES: 'Credit Notes',
  CONSIGNMENT: 'Consignment & Vendors',
  ACCOUNTING: 'Accounting',
};

export const PERMISSION_ACTION_LABELS: Record<PermissionActionType, string> = {
  VIEW: 'View',
  CREATE: 'Create',
  EDIT: 'Edit',
  DELETE: 'Delete',
  EXPORT: 'Export',
  APPROVE: 'Approve',
};

// Role templates for quick permission assignment
export const ROLE_TEMPLATES = {
  // Full access to all modules and actions
  FULL_ACCESS: {
    TRAVEL: Object.fromEntries(
      TRAVEL_MODULES.map((m) => [m, [...PERMISSION_ACTIONS]])
    ) as Record<TravelModule, PermissionActionType[]>,
    TRADING: Object.fromEntries(
      TRADING_MODULES.map((m) => [m, [...PERMISSION_ACTIONS]])
    ) as Record<TradingModule, PermissionActionType[]>,
  },

  // Sales Manager - Full sales access, view-only for others
  SALES_MANAGER: {
    TRAVEL: {
      DASHBOARD: ['VIEW'],
      CUSTOMERS: ['VIEW', 'CREATE', 'EDIT'],
      PARTNERS: ['VIEW'],
      PACKAGES: ['VIEW'],
      QUOTES: ['VIEW', 'CREATE', 'EDIT', 'DELETE', 'EXPORT'],
      BOOKINGS: ['VIEW', 'CREATE', 'EDIT', 'EXPORT'],
      INVOICES: ['VIEW', 'CREATE', 'EDIT', 'EXPORT'],
      EXPENSES: ['VIEW'],
      ALLOCATIONS: ['VIEW'],
      OPERATIONS: ['VIEW'],
      NOTIFICATIONS: ['VIEW'],
      PROMO_CODES: ['VIEW', 'CREATE', 'EDIT'],
      REVIEWS: ['VIEW'],
      ACCOUNTING: ['VIEW'],
    } as Record<TravelModule, PermissionActionType[]>,
    TRADING: {
      DASHBOARD: ['VIEW'],
      CUSTOMERS: ['VIEW', 'CREATE', 'EDIT'],
      BRANDS: ['VIEW'],
      MODELS: ['VIEW'],
      PRODUCTS: ['VIEW'],
      INQUIRIES: ['VIEW', 'CREATE', 'EDIT'],
      WAREHOUSES: ['VIEW'],
      STOCK_LEVELS: ['VIEW'],
      STOCK_MOVEMENTS: ['VIEW'],
      SUPPLIERS: ['VIEW'],
      PURCHASE_ORDERS: ['VIEW'],
      GOODS_RECEIPTS: ['VIEW'],
      SALES_QUOTES: ['VIEW', 'CREATE', 'EDIT', 'DELETE', 'EXPORT'],
      SALES_ORDERS: ['VIEW', 'CREATE', 'EDIT', 'EXPORT'],
      DELIVERIES: ['VIEW', 'CREATE', 'EDIT'],
      INVOICES: ['VIEW', 'CREATE', 'EDIT', 'EXPORT'],
      PAYMENTS: ['VIEW', 'CREATE', 'EDIT'],
      SUPPLIER_INVOICES: ['VIEW'],
      CREDIT_NOTES: ['VIEW', 'CREATE', 'EDIT'],
      CONSIGNMENT: [],
      ACCOUNTING: ['VIEW'],
    } as Record<TradingModule, PermissionActionType[]>,
  },

  // Agent - Basic sales operations
  AGENT: {
    TRAVEL: {
      DASHBOARD: ['VIEW'],
      CUSTOMERS: ['VIEW', 'CREATE', 'EDIT'],
      PARTNERS: ['VIEW'],
      PACKAGES: ['VIEW'],
      QUOTES: ['VIEW', 'CREATE', 'EDIT'],
      BOOKINGS: ['VIEW', 'CREATE', 'EDIT'],
      INVOICES: ['VIEW'],
      EXPENSES: [],
      ALLOCATIONS: ['VIEW'],
      OPERATIONS: ['VIEW'],
      NOTIFICATIONS: ['VIEW'],
      PROMO_CODES: ['VIEW'],
      REVIEWS: ['VIEW'],
      ACCOUNTING: [],
    } as Record<TravelModule, PermissionActionType[]>,
    TRADING: {
      DASHBOARD: ['VIEW'],
      CUSTOMERS: ['VIEW', 'CREATE', 'EDIT'],
      BRANDS: ['VIEW'],
      MODELS: ['VIEW'],
      PRODUCTS: ['VIEW'],
      INQUIRIES: ['VIEW', 'CREATE', 'EDIT'],
      WAREHOUSES: ['VIEW'],
      STOCK_LEVELS: ['VIEW'],
      STOCK_MOVEMENTS: [],
      SUPPLIERS: ['VIEW'],
      PURCHASE_ORDERS: [],
      GOODS_RECEIPTS: [],
      SALES_QUOTES: ['VIEW', 'CREATE', 'EDIT'],
      SALES_ORDERS: ['VIEW'],
      DELIVERIES: ['VIEW'],
      INVOICES: ['VIEW'],
      PAYMENTS: ['VIEW'],
      SUPPLIER_INVOICES: [],
      CREDIT_NOTES: [],
      CONSIGNMENT: [],
      ACCOUNTING: [],
    } as Record<TradingModule, PermissionActionType[]>,
  },

  // Accountant - Full accounting access
  ACCOUNTANT: {
    TRAVEL: {
      DASHBOARD: ['VIEW'],
      CUSTOMERS: ['VIEW'],
      PARTNERS: ['VIEW'],
      PACKAGES: ['VIEW'],
      QUOTES: ['VIEW'],
      BOOKINGS: ['VIEW'],
      INVOICES: ['VIEW', 'CREATE', 'EDIT', 'DELETE', 'EXPORT'],
      EXPENSES: ['VIEW', 'CREATE', 'EDIT', 'DELETE', 'EXPORT'],
      ALLOCATIONS: ['VIEW'],
      OPERATIONS: ['VIEW'],
      NOTIFICATIONS: ['VIEW'],
      PROMO_CODES: [],
      REVIEWS: [],
      ACCOUNTING: ['VIEW', 'CREATE', 'EDIT', 'DELETE', 'EXPORT', 'APPROVE'],
    } as Record<TravelModule, PermissionActionType[]>,
    TRADING: {
      DASHBOARD: ['VIEW'],
      CUSTOMERS: ['VIEW', 'EXPORT'],
      BRANDS: ['VIEW'],
      MODELS: ['VIEW'],
      PRODUCTS: ['VIEW'],
      INQUIRIES: ['VIEW'],
      WAREHOUSES: ['VIEW'],
      STOCK_LEVELS: ['VIEW'],
      STOCK_MOVEMENTS: ['VIEW'],
      SUPPLIERS: ['VIEW'],
      PURCHASE_ORDERS: ['VIEW', 'EXPORT'],
      GOODS_RECEIPTS: ['VIEW', 'EXPORT'],
      SALES_QUOTES: ['VIEW'],
      SALES_ORDERS: ['VIEW', 'EXPORT'],
      DELIVERIES: ['VIEW'],
      INVOICES: ['VIEW', 'CREATE', 'EDIT', 'DELETE', 'EXPORT'],
      PAYMENTS: ['VIEW', 'CREATE', 'EDIT', 'DELETE', 'EXPORT'],
      SUPPLIER_INVOICES: ['VIEW', 'CREATE', 'EDIT', 'DELETE', 'EXPORT'],
      CREDIT_NOTES: ['VIEW', 'CREATE', 'EDIT', 'DELETE', 'EXPORT', 'APPROVE'],
      CONSIGNMENT: [],
      ACCOUNTING: ['VIEW', 'CREATE', 'EDIT', 'DELETE', 'EXPORT', 'APPROVE'],
    } as Record<TradingModule, PermissionActionType[]>,
  },

  // Operations - Inventory and operations management
  OPERATIONS: {
    TRAVEL: {
      DASHBOARD: ['VIEW'],
      CUSTOMERS: ['VIEW'],
      PARTNERS: ['VIEW', 'CREATE', 'EDIT'],
      PACKAGES: ['VIEW', 'CREATE', 'EDIT'],
      QUOTES: ['VIEW'],
      BOOKINGS: ['VIEW', 'EDIT'],
      INVOICES: ['VIEW'],
      EXPENSES: ['VIEW', 'CREATE', 'EDIT'],
      ALLOCATIONS: ['VIEW', 'CREATE', 'EDIT', 'DELETE'],
      OPERATIONS: ['VIEW', 'CREATE', 'EDIT', 'DELETE', 'EXPORT'],
      NOTIFICATIONS: ['VIEW', 'CREATE'],
      PROMO_CODES: [],
      REVIEWS: ['VIEW'],
      ACCOUNTING: [],
    } as Record<TravelModule, PermissionActionType[]>,
    TRADING: {
      DASHBOARD: ['VIEW'],
      CUSTOMERS: ['VIEW', 'CREATE', 'EDIT'],
      BRANDS: ['VIEW', 'CREATE', 'EDIT'],
      MODELS: ['VIEW', 'CREATE', 'EDIT'],
      PRODUCTS: ['VIEW', 'CREATE', 'EDIT'],
      INQUIRIES: ['VIEW'],
      WAREHOUSES: ['VIEW', 'CREATE', 'EDIT', 'DELETE'],
      STOCK_LEVELS: ['VIEW', 'CREATE', 'EDIT'],
      STOCK_MOVEMENTS: ['VIEW', 'CREATE', 'EDIT', 'DELETE', 'EXPORT'],
      SUPPLIERS: ['VIEW', 'CREATE', 'EDIT'],
      PURCHASE_ORDERS: ['VIEW', 'CREATE', 'EDIT', 'APPROVE'],
      GOODS_RECEIPTS: ['VIEW', 'CREATE', 'EDIT'],
      SALES_QUOTES: ['VIEW'],
      SALES_ORDERS: ['VIEW', 'EDIT'],
      DELIVERIES: ['VIEW', 'CREATE', 'EDIT', 'DELETE'],
      INVOICES: [],
      PAYMENTS: [],
      SUPPLIER_INVOICES: ['VIEW'],
      CREDIT_NOTES: [],
      CONSIGNMENT: [],
      ACCOUNTING: [],
    } as Record<TradingModule, PermissionActionType[]>,
  },

  // Viewer - Read-only access
  VIEWER: {
    TRAVEL: Object.fromEntries(
      TRAVEL_MODULES.map((m) => [m, ['VIEW'] as PermissionActionType[]])
    ) as Record<TravelModule, PermissionActionType[]>,
    TRADING: Object.fromEntries(
      TRADING_MODULES.map((m) => [m, ['VIEW'] as PermissionActionType[]])
    ) as Record<TradingModule, PermissionActionType[]>,
  },
};
